import { Link } from "react-router-dom";

export default function Privacy() {
  const effectiveDate = "25 May 2026";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="container max-w-3xl py-12 md:py-20">
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Back to home</Link>
        <h1 className="text-4xl font-bold mt-6 mb-2">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Effective date: {effectiveDate}</p>

        <div className="prose prose-sm md:prose-base max-w-none space-y-6 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:text-muted-foreground [&_li]:text-muted-foreground [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1">
          <section>
            <p>
              Hound Payments ("Hound Payments", "we", "us", "our") respects your privacy and is committed to
              protecting your personal data. This Privacy Policy explains how we collect, use, store, and share
              information when you use our website, mobile applications (iOS and Android), and related services
              (together, the "Service").
            </p>
            <p>
              We are the data controller for personal data processed through the Service. If you have any questions,
              contact us at <a className="underline" href="mailto:hello@houndpayments.co.uk">hello@houndpayments.co.uk</a>.
            </p>
          </section>

          <section>
            <h2>1. Information We Collect</h2>

            <h3>a. Information you provide</h3>
            <ul>
              <li><strong>Account data:</strong> name, email address, password, business name, phone number.</li>
              <li><strong>Payment settings:</strong> the bank account details you choose to share with your own customers (e.g. sort code, account number, payment reference). We do not process card payments.</li>
              <li><strong>Customer data you upload:</strong> your customers' names, email addresses, phone numbers, job descriptions, amounts charged, photos of completed work, and payment status.</li>
              <li><strong>Communications:</strong> messages you send to support, feedback, and any content you submit.</li>
            </ul>

            <h3>b. Information collected automatically</h3>
            <ul>
              <li><strong>Device & usage data:</strong> device type, operating system, app version, IP address, crash logs, and basic usage analytics.</li>
              <li><strong>Cookies & similar technologies:</strong> on the web, we use essential cookies to keep you signed in. We do not use advertising cookies.</li>
            </ul>

            <h3>c. Information from third parties</h3>
            <ul>
              <li><strong>Authentication providers:</strong> if you sign in via a third party, we receive your basic profile information.</li>
              <li><strong>Messaging providers:</strong> delivery status from our SMS and email providers when reminders or receipts are sent.</li>
            </ul>
          </section>

          <section>
            <h2>2. How We Use Your Information</h2>
            <ul>
              <li>To provide, operate, and maintain the Service.</li>
              <li>To send invoices, payment requests, receipts, review requests, and automated reminders to the customers you add.</li>
              <li>To authenticate you and secure your account.</li>
              <li>To respond to support requests and communicate service updates.</li>
              <li>To detect, prevent, and investigate fraud, abuse, or security incidents.</li>
              <li>To comply with legal obligations including tax, accounting, and anti-money-laundering requirements.</li>
              <li>To improve the Service through aggregated, anonymised analytics.</li>
            </ul>
          </section>

          <section>
            <h2>3. Legal Bases (UK & EU users)</h2>
            <p>We process personal data under the UK GDPR and EU GDPR on these legal bases:</p>
            <ul>
              <li><strong>Contract:</strong> to provide the Service you signed up for.</li>
              <li><strong>Legitimate interests:</strong> to secure, improve, and market the Service.</li>
              <li><strong>Consent:</strong> where required (e.g. optional marketing emails) — you can withdraw at any time.</li>
              <li><strong>Legal obligation:</strong> to comply with applicable laws.</li>
            </ul>
          </section>

          <section>
            <h2>4. How We Share Information</h2>
            <p>We do not sell your personal data. We share data only with:</p>
            <ul>
              <li><strong>Your customers:</strong> the bank/payment details and messages you ask us to send on your behalf.</li>
              <li><strong>Service providers (processors):</strong> hosting (Supabase / Lovable Cloud), SMS gateway, email delivery, analytics, and crash reporting. They are contractually required to protect your data and only use it to provide their service to us.</li>
              <li><strong>Legal & safety:</strong> where required by law, court order, or to protect rights, property, or safety.</li>
              <li><strong>Business transfers:</strong> in connection with a merger, acquisition, or sale of assets, with notice to you.</li>
            </ul>
          </section>

          <section>
            <h2>5. International Transfers</h2>
            <p>
              Your data is primarily stored in the United Kingdom and European Economic Area. Where data is transferred
              outside the UK/EEA, we rely on adequacy decisions or Standard Contractual Clauses to protect it.
            </p>
          </section>

          <section>
            <h2>6. Data Retention</h2>
            <p>
              We keep your account and customer data for as long as your account is active. If you close your account,
              we delete or anonymise your data within 90 days, except where we are required to retain it for legal,
              accounting, or fraud-prevention purposes.
            </p>
          </section>

          <section>
            <h2>7. Your Rights</h2>
            <p>Depending on where you live, you may have the right to:</p>
            <ul>
              <li>Access the personal data we hold about you.</li>
              <li>Correct inaccurate data.</li>
              <li>Delete your data ("right to be forgotten").</li>
              <li>Restrict or object to processing.</li>
              <li>Data portability.</li>
              <li>Withdraw consent at any time.</li>
              <li>Lodge a complaint with the UK Information Commissioner's Office (ICO) or your local data protection authority.</li>
            </ul>
            <p>To exercise your rights, email <a className="underline" href="mailto:hello@houndpayments.co.uk">hello@houndpayments.co.uk</a>. We respond within 30 days.</p>
          </section>

          <section>
            <h2>8. Children</h2>
            <p>
              The Service is not directed to children under 16, and we do not knowingly collect personal data from
              children. If you believe a child has provided us data, contact us and we will delete it.
            </p>
          </section>

          <section>
            <h2>9. Security</h2>
            <p>
              We use industry-standard security measures including encryption in transit (TLS), encrypted database
              storage, access controls, and regular security reviews. No system is 100% secure; please use a strong,
              unique password.
            </p>
          </section>

          <section>
            <h2>10. Mobile App Permissions</h2>
            <p>Our iOS and Android apps may request the following permissions:</p>
            <ul>
              <li><strong>Camera & Photos:</strong> to upload photos of completed jobs to invoices. Photos are only uploaded when you choose them.</li>
              <li><strong>Contacts (optional):</strong> to let you quickly add a customer. Contacts are read on-device and only the entries you select are uploaded.</li>
              <li><strong>Notifications:</strong> to alert you when a customer pays or a reminder is sent.</li>
            </ul>
            <p>You can revoke any permission at any time in your device settings.</p>
          </section>

          <section>
            <h2>11. Third-Party Services</h2>
            <p>
              The Service may link to third-party websites (for example, review platforms). Their privacy practices are
              governed by their own policies, and we are not responsible for them.
            </p>
          </section>

          <section>
            <h2>12. Changes to this Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we do, we will update the "Effective date" at the
              top and, for material changes, notify you in-app or by email.
            </p>
          </section>

          <section>
            <h2>13. Contact Us</h2>
            <p>
              Hound Payments<br />
              United Kingdom<br />
              Email: <a className="underline" href="mailto:hello@houndpayments.co.uk">hello@houndpayments.co.uk</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
